import { type ActorRefFromLogic, waitFor } from "xstate";

import type { User } from "~/models/user";
import { ALL_USERS, type DocPermRequest, type Email } from "~/state/types";
import { createDocsApiFetch } from "~/utils/docsapi";

import { type AuthenticationMachine, authnStateNotUnknown } from "../authentication";
import { isEmail } from "../permissions/isEmail";

export const setRepoPublished = async ({
	input: { type, workspaceName, documentId, user, authenticationActorRef, skip },
}: {
	input: {
		type: "grant" | "revoke";
		workspaceName: string;
		documentId: string;
		user: User | Email | typeof ALL_USERS;
		authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
		skip: boolean;
	};
}) => {
	if (skip) {
		return;
	}

	const authSnap = await waitFor(authenticationActorRef, authnStateNotUnknown);
	const token = await authSnap.context.getAccessTokenSilently();
	const publishWorkspace =
		workspaceName ||
		(authSnap.context.authnStatus === "authenticated" && authSnap.context.user.nickname) ||
		"";
	if (!token) {
		return;
	}

	// if granting public permissions, we use a special query parameter to avoid errors
	const allUsersFlag = user === ALL_USERS ? `?all=true` : "";

	let body: DocPermRequest;
	if (user === ALL_USERS) {
		body = {
			userType: "AllUsers",
			relation: "reader",
			value: "",
		};
	} else if (isEmail(user)) {
		body = {
			userType: "Email",
			relation: "reader",
			value: user.value,
		};
	} else {
		body = {
			userType: "Username",
			relation: "reader",
			value: user.giteaUserId,
		};
	}

	const client = createDocsApiFetch(token);
	return await client(`/permissions/${publishWorkspace}/${documentId}${allUsersFlag}`, {
		method: type === "grant" ? "POST" : "DELETE",
		body: JSON.stringify(body),
	});
};
